import { generateMultiSheetExcelFromData } from '../../../../../utils/excel-from-data';

import { OperatorRow } from '../types';
import { shiftRowDescription,
  formatHHMM,
  formatEstimatedHours,
  formatDateTime,
  sumMinutes,
  groupOrdersByDay,
  dayEntriesLabel,
} from '../utils';

const KIND_EXTERNAL = 'external-estimated';

interface ExportContext {
  rows: OperatorRow[];
  teamNameById: Record<string, string>;
  filters: { fromDate: string; toDate: string; states: string[] };
  /** Feature flag `showHoursType`: si OFF, no se incluye la columna "Desglose
   *  por tipo" (igual que la tabla y el PDF). */
  showHoursType: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Estado de la OT como TEXTO traducido (`common:states.<state>`). En el export
 * no hay tag de color — solo el texto del estado, igual que el header de
 * columna usa `tables:headers.status`. Vacío/null → "—".
 */
const stateLabel = (
  state: string | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => (state ? t(`states.${state}`, { ns: 'common' }) : '—');

const fmtDate = (iso: string): string =>
  iso ? new Date(iso).toLocaleDateString() : '—';

/**
 * Export Excel del informe Gestió Horària.
 *
 * Reutiliza el helper canónico `generateMultiSheetExcelFromData`
 * (commit 7 del Plan-v2 — hermano de `generateExcelFromData`, ambos en
 * src/utils/excel-from-data.ts), para cuatro hojas:
 *
 *   1. "Informe" — metadatos (rango/filtros/totales separados).
 *   2. "Resumen" — una fila por operario con totales agregados.
 *   3. "Detalle OTs" — una fila por OT (operario × OT), con minutos
 *      y formato HH:MM.
 *   4. "Por trabajador y día" — una fila por sujeto×día (agregación
 *      compartida con el PDF vía groupOrdersByDay).
 *
 * Compartiendo el helper, futuros reports agregados se benefician y
 * el formato Excel queda homogéneo en todo el proyecto.
 */
export async function generateLaborHoursExcel({
  rows,
  teamNameById,
  filters,
  showHoursType,
  t,
}: ExportContext): Promise<void> {
  const summaryHeaders = [
    t('forms:timeManagement.headers.subject'),
    t('tables:headers.type'),
    t('tables:headers.team'),
    t('forms:timeManagement.headers.operatorsNumber'),
    t('tables:headers.totalHours'),
    t('forms:timeManagement.export.headerTotalMinutes'),
    // "Desglose por tipo" solo si el feature flag de tipos de hora está activo.
    ...(showHoursType ? [t('forms:timeManagement.export.headerBreakdown')] : []),
  ];
  const summaryRows: string[][] = rows.map((r) => {
    const isExternal = r.kind === 'external-estimated';
    return [
      r.subjectName,
      isExternal
        ? t('forms:timeManagement.kind.external')
        : t('forms:timeManagement.kind.internal'),
      (r.teamIDsSnapshot ?? [])
        .map((id) => teamNameById[id] ?? id)
        .filter(Boolean)
        .join(', '),
      r.operatorsNumber != null ? String(r.operatorsNumber) : '',
      // Para externos el total es combinado (todos los operarios); se etiqueta
      // para que en el Excel no se confunda con horas por operario.
      isExternal
        ? `${formatHHMM(r.totalMinutes)} (${t(
            'forms:timeManagement.totalAllOperators',
          )})`
        : formatHHMM(r.totalMinutes),
      String(r.totalMinutes),
      ...(showHoursType
        ? [
            r.byHourType
              .map((b) => `${b.hourType}: ${formatHHMM(b.minutes)}`)
              .join(' / '),
          ]
        : []),
    ];
  });

  // Detalle por OT. Para externos se desglosa la fórmula
  // Duración × Operarios = Subtotal (combinado); para internos esas dos
  // columnas quedan vacías y el "Total horas" son las horas reales.
  const detailHeaders = [
    t('forms:timeManagement.headers.subject'),
    t('tables:headers.type'),
    t('tables:headers.roid'),
    t('tables:headers.status'),
    t('simultaneous', { ns: 'common' }),
    t('forms:timeManagement.headers.predefined'),
    t('forms:timeManagement.headers.description'),
    t('forms:workOrder.startDate'),
    t('forms:workOrder.endDate'),
    t('forms:timeManagement.headers.duration'),
    t('forms:timeManagement.headers.operatorsNumber'),
    t('tables:headers.totalHours'),
    t('forms:timeManagement.export.headerTotalMinutes'),
  ];
  const detailRows: string[][] = [];
  for (const r of rows) {
    const isExternal = r.kind === KIND_EXTERNAL;
    for (const o of r.orders) {
      detailRows.push([
        r.subjectName,
        isExternal
          ? t('forms:timeManagement.kind.external')
          : t('forms:timeManagement.kind.internal'),
        o.roid,
        stateLabel(o.state, t),
        // Distintivo: turno de RV (labor-entry-rv 2026-07-21) o simultánea.
        o.sourceType === 'surveillanceRoute'
          ? t('orderTypes.surveillanceRoute', { ns: 'common' })
          : o.isSimultaneous
            ? t('simultaneous', { ns: 'common' })
            : '',
        o.predefined ?? '',
        // Turno RV: descripción + nº de tramos + descuento por actuaciones.
        shiftRowDescription(o, t, ''),
        formatDateTime(o.startTime),
        formatDateTime(o.endTime),
        o.durationMinutes != null ? formatHHMM(o.durationMinutes) : '',
        o.operatorsNumber != null ? String(o.operatorsNumber) : '',
        formatHHMM(o.minutes),
        String(o.minutes),
      ]);
    }
  }

  // Hoja "Por trabajador y día": una fila por sujeto×día. La agregación vive
  // en groupOrdersByDay (compartida con la tabla por día del PDF).
  const entryLabels = {
    simultaneous: t('simultaneous', { ns: 'common' }),
    surveillanceRoute: t('orderTypes.surveillanceRoute', { ns: 'common' }),
  };
  const byDayHeaders = [
    t('forms:timeManagement.headers.subject'),
    t('tables:headers.type'),
    t('tables:headers.team'),
    t('tables:headers.date'),
    t('forms:timeManagement.export.headerDayTotal'),
    t('forms:timeManagement.export.headerTotalMinutes'),
    t('forms:timeManagement.export.headerOtCount'),
    t('forms:timeManagement.export.headerOrdersDetail'),
  ];
  const byDayRows: string[][] = [];
  for (const r of rows) {
    const isExternal = r.kind === KIND_EXTERNAL;
    const teamNames = (r.teamIDsSnapshot ?? [])
      .map((id) => teamNameById[id] ?? id)
      .filter(Boolean)
      .join(', ');
    for (const g of groupOrdersByDay(r)) {
      byDayRows.push([
        r.subjectName,
        isExternal
          ? t('forms:timeManagement.kind.external')
          : t('forms:timeManagement.kind.internal'),
        teamNames,
        g.dayLabel,
        isExternal
          ? formatEstimatedHours(g.totalMinutes)
          : formatHHMM(g.totalMinutes),
        String(g.totalMinutes),
        String(g.otCount),
        dayEntriesLabel(g.entries, entryLabels),
      ]);
    }
  }

  // Hoja "Informe": metadatos para que, al abrir o reenviar el Excel, se sepa
  // SIEMPRE a qué rango/filtros corresponde (antes solo estaba en el nombre del
  // fichero) + los totales SEPARADOS reales/estimadas (nunca un total mixto).
  const internalMinutes = sumMinutes(rows.filter((r) => r.kind !== KIND_EXTERNAL));
  const externalMinutes = sumMinutes(rows.filter((r) => r.kind === KIND_EXTERNAL));
  const infoRows: string[][] = [
    [
      t('forms:timeManagement.filters.dateRange'),
      `${fmtDate(filters.fromDate)} → ${fmtDate(filters.toDate)}`,
    ],
    [
      t('tables:headers.status'),
      filters.states.length > 0
        ? filters.states.map((s) => stateLabel(s, t)).join(', ')
        : '—',
    ],
    [t('forms:timeManagement.export.pdfGenerated'), new Date().toLocaleString()],
    ['', ''],
    [t('forms:timeManagement.summary.internalReal'), formatHHMM(internalMinutes)],
    [
      t('forms:timeManagement.summary.externalEstimated'),
      formatEstimatedHours(externalMinutes),
    ],
    [t('forms:timeManagement.estimationNote'), ''],
  ];

  await generateMultiSheetExcelFromData(
    [
      {
        name: t('forms:timeManagement.export.sheetInfo'),
        headers: [t('forms:timeManagement.export.pdfTitle'), ''],
        rows: infoRows,
      },
      {
        name: t('forms:timeManagement.export.sheetSummary'),
        headers: summaryHeaders,
        rows: summaryRows,
      },
      {
        name: t('forms:timeManagement.export.sheetDetail'),
        headers: detailHeaders,
        rows: detailRows,
      },
      {
        name: t('forms:timeManagement.export.sheetByDay'),
        headers: byDayHeaders,
        rows: byDayRows,
      },
    ],
    {
      filename: `gestion-horaria_${filters.fromDate.slice(0, 10)}_${filters.toDate.slice(0, 10)}.xlsx`,
    },
  );
}
